import { t } from "@lingui/macro";
import {
  ClipboardText,
  PaintBrush,
  ShareFat,
  HouseLine,
  ReadCvLogo,
  UserPlus,
} from "@phosphor-icons/react";
import { Button, ScrollArea } from "@reactive-resume/ui";
import { useRef } from "react";
import { Link, useNavigate } from "react-router";
import { cn } from "@reactive-resume/utils";

import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useResumeStore } from "@/client/stores/resume";
import { SidebarItems, useViewStore } from "@/client/stores/view";
import { Logo } from "@/client/components/logo";
import { SidebarContent } from "./sections/content";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useTheme } from "@reactive-resume/hooks";
import { useAuthStore } from "@/client/stores/auth";

type NavItem = {
  id: SidebarItems;
  icon: React.ReactNode;
};

const activeStyles = "bg-primary/10 hover:bg-primary/15 border border-primary/20";
const activeStylesDark = "bg-primary/15 hover:bg-primary/20 border border-primary/30";
const navItems: NavItem[] = [
  {
    id: "data",
    icon: <ClipboardText size={24} weight="duotone" />,
  },
  {
    id: "design",
    icon: <PaintBrush size={24} weight="duotone" />,
  },
  {
    id: "share",
    icon: <ShareFat size={24} weight="duotone" />,
  },
];

export const LeftSidebar = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const setActiveView = useViewStore((state) => state.setActiveView);
  const activeSidebar = useViewStore((state) => state.activeSidebar);
  const setActiveSidebar = useViewStore((state) => state.setActiveSidebar);

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => state.resume.data.sections.custom);
  const { isDarkMode } = useTheme();
  const user = useAuthStore((state) => state.user);

  const handleNavItemClick = (item: NavItem) => {
    setActiveSidebar(item.id);
    if (item.id === "data") {
      setActiveView("analytics");
    } else if (item.id === "design") {
      setActiveView("builder");
    }
  };

  const getLabel = (id: SidebarItems) => {
    switch (id) {
      case "data":
        return t`Data`;
      case "design":
        return t`Design`;
      case "share":
        return t`Share`;
    }
  };

  return (
    <div className="bg-secondary-accent flex">
      {/* Primary Sidebar */}
      <div className="bg-background/65 flex basis-24 flex-col items-center justify-between py-4">
        <div className="flex flex-col items-center">
          <Link className="flex size-full flex-col items-center text-lg font-bold" to="/dashboard">
            <Logo size={42} />
            cvkit
          </Link>
        </div>

        <div className="flex flex-col items-center justify-center gap-y-4">
          {/* Home Button for authenticated users or Sign Up button for anonymous users */}
          {user && !user.isAnonymous ? (
            <button
              className={cn(
                "group relative flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-y-1 rounded-sm transition-all",
                "hover:bg-foreground/5 focus:bg-foreground/5 active:bg-foreground/10",
              )}
              onClick={() => navigate("/dashboard")}
            >
              <div className="text-foreground/60 group-hover:text-foreground transition-colors">
                <HouseLine size={24} weight="duotone" />
              </div>
              <span className="text-foreground/60 group-hover:text-foreground text-xs font-medium transition-colors">
                {t`Home`}
              </span>
            </button>
          ) : (
            <button
              className={cn(
                "group relative flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-y-1 rounded-sm transition-all",
                "hover:bg-primary/10 focus:bg-primary/10 active:bg-primary/15 border-primary/20 border",
                "from-primary/5 to-primary/10 bg-gradient-to-br",
              )}
              onClick={() => navigate("/auth/register")}
            >
              <div className="text-primary group-hover:text-primary/80 transition-colors">
                <UserPlus size={24} weight="duotone" />
              </div>
              <span className="text-primary group-hover:text-primary/80 text-xs font-medium transition-colors">
                {t`Sign Up`}
              </span>
            </button>
          )}

          {/* Separator Line */}
          <div className="bg-foreground/20 h-px w-12"></div>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={cn(
                "group relative flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-y-1 rounded-sm transition-all",
                activeSidebar === item.id
                  ? `${isDarkMode ? activeStylesDark : activeStyles}`
                  : `hover:bg-foreground/5 focus:bg-foreground/5 active:bg-foreground/10`,
              )}
              onClick={() => handleNavItemClick(item)}
            >
              <div
                className={cn(
                  "transition-colors",
                  activeSidebar === item.id
                    ? "text-primary"
                    : "text-foreground/60 group-hover:text-foreground",
                )}
              >
                {activeSidebar === item.id ? (
                  <div className="relative scale-110">
                    {item.icon}
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-amber-300/50 blur-sm dark:from-purple-50/50 dark:to-purple-300/50" />
                  </div>
                ) : (
                  item.icon
                )}
              </div>
              <span
                className={`text-xs font-medium transition-colors ${
                  activeSidebar === item.id
                    ? "scale-110 text-amber-900 dark:text-purple-100"
                    : "text-foreground/60 group-hover:text-foreground"
                }`}
              >
                {getLabel(item.id)}
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-col items-center justify-center gap-y-4">
          <ThemeSwitch size={18} />
          <UserOptions>
            <Button
              size="icon"
              variant="ghost"
              className="hover:bg-foreground/10 active:bg-foreground/15 rounded-md"
            >
              <UserAvatar size={36} />
            </Button>
          </UserOptions>
        </div>
      </div>

      {/* Content Area */}
      <ScrollArea
        ref={scrollAreaRef}
        orientation="vertical"
        className="h-screen flex-1 pb-16 lg:pb-0"
      >
        <div ref={containerRef}>
          <SidebarContent customSections={customSections} addSection={addSection} />
        </div>
      </ScrollArea>
    </div>
  );
};
